/**
 * Черновики разделов — §6.
 *
 * Прямой перенос паттерна из демо (`committedRef`, строки 5508–5527), но
 * поверх базы, где параллельно работает кто-то ещё.
 *
 * Как это работает и почему именно так:
 *
 *   - правки применяются в интерфейсе СРАЗУ и видны немедленно, но считаются
 *     черновыми до нажатия «Сохранить изменения». Это осознанное решение
 *     заказчика: техник правит десяток строк подряд и не хочет ждать сети
 *     после каждого поля;
 *   - рядом с пунктом меню горит точка, если в разделе есть несохранённое;
 *   - уход в другой раздел с несохранённым перехватывается модальным окном
 *     с тремя вариантами: сохранить и перейти / не сохранять / остаться;
 *   - «не сохранять» откатывает черновик к последнему сохранённому состоянию —
 *     поэтому серверный снимок хранится отдельно и никогда не мутируется.
 *
 * Отправляется на сервер МИНИМАЛЬНЫЙ патч, а не запись целиком: так в журнале
 * изменений видно, что именно правил пользователь, а не «обновил всё».
 */

import { create } from 'zustand';
import type { ModuleName } from '@otc/shared';
import type { ModuleCommitPayload, ModuleCommitResult } from '../api/client.js';

/** Любая версионируемая сущность. */
export interface Entity {
  id: string;
  version: number;
  [key: string]: unknown;
}

/** Записи одного вида: `clinic`, `legal`, `branch`… */
type ByKind = Record<string, Record<string, Entity>>;

interface ModuleState {
  /** Последнее известное состояние сервера. Не мутируется никогда. */
  server: ByKind;
  /** Рабочая копия с правками пользователя. */
  draft: ByKind;
  /** Новые записи, ещё не отправленные: ключ — временный идентификатор. */
  created: Record<string, { kind: string; data: Record<string, unknown> }>;
  /** Помеченные на удаление существующие записи. */
  deleted: Record<string, string>;
}

interface DraftStore {
  modules: Partial<Record<ModuleName, ModuleState>>;

  /** Загрузка серверных данных. Сбрасывает черновик — вызывается при обновлении справочников. */
  load(module: ModuleName, kind: string, entities: Entity[]): void;

  update(module: ModuleName, kind: string, id: string, patch: Record<string, unknown>): void;
  create(module: ModuleName, kind: string, data: Record<string, unknown>): string;
  remove(module: ModuleName, kind: string, id: string): void;

  /** Откат к последнему сохранённому состоянию — кнопка «Не сохранять». */
  discard(module: ModuleName): void;

  /**
   * Перенос СВОИХ правок на свежую версию записи — «оставить моё».
   *
   * Меняется только номер версии, значения полей снимка остаются прежними.
   * Это и даёт нужный разбор: в пакет уходят ровно те поля, которые правил
   * пользователь, а поля, тронутые другим сотрудником, в пакет не попадают
   * и сохраняют его значения. «Оставить моё» означает «мои поля», а не
   * «мой снимок целиком».
   */
  rebase(
    module: ModuleName,
    conflicts: { kind: string; id: string; actualVersion: number | null }[],
  ): void;

  /** Приведение серверного снимка к состоянию после успешного сохранения. */
  applyCommit(module: ModuleName, result: ModuleCommitResult): void;

  isDirty(module: ModuleName): boolean;
  buildCommit(module: ModuleName): ModuleCommitPayload;

  /**
   * Текущее состояние записей вида — с учётом правок, создания и удаления.
   *
   * Ограничение на `T` намеренно минимальное: экраны описывают свои строки
   * обычными интерфейсами без индексной сигнатуры, и требовать её от каждого
   * экрана значило бы ослаблять типизацию ради внутреннего представления
   * хранилища.
   */
  entities<T extends { id: string; version: number }>(module: ModuleName, kind: string): T[];
  entity<T extends { id: string; version: number }>(
    module: ModuleName,
    kind: string,
    id: string,
  ): T | undefined;
}

const emptyModule = (): ModuleState => ({ server: {}, draft: {}, created: {}, deleted: {} });

let tempCounter = 0;
/** Временный идентификатор новой записи. Префикс `tmp_` распознаёт сервер. */
function nextTempId(): string {
  tempCounter += 1;
  return `tmp_${tempCounter}_${Math.random().toString(36).slice(2, 8)}`;
}

/** Сравнение значений полей, включая массивы связей (`doctor.clinics`). */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (typeof a === 'object') return JSON.stringify(a) === JSON.stringify(b);
  return false;
}

/** Минимальный патч: только поля, которые действительно отличаются от сервера. */
function diffEntity(server: Entity, draft: Entity): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(draft)) {
    if (key === 'id' || key === 'version') continue;
    if (!sameValue(server[key], value)) patch[key] = value;
  }
  return patch;
}

export const useDrafts = create<DraftStore>((set, get) => ({
  modules: {},

  load(module, kind, entities) {
    set((state) => {
      const current = state.modules[module] ?? emptyModule();
      const map: Record<string, Entity> = {};
      for (const entity of entities) map[entity.id] = entity;

      return {
        modules: {
          ...state.modules,
          [module]: {
            ...current,
            server: { ...current.server, [kind]: map },
            // Черновик получает свежую копию: пользовательские правки этого
            // вида, если они были, затираются. Загрузка происходит только
            // после успешного сохранения или явного обновления данных.
            draft: { ...current.draft, [kind]: { ...map } },
          },
        },
      };
    });
  },

  update(module, kind, id, patch) {
    set((state) => {
      const current = state.modules[module] ?? emptyModule();

      // Правка новой, ещё не сохранённой записи идёт в `created`.
      if (current.created[id]) {
        return {
          modules: {
            ...state.modules,
            [module]: {
              ...current,
              created: {
                ...current.created,
                [id]: { ...current.created[id]!, data: { ...current.created[id]!.data, ...patch } },
              },
            },
          },
        };
      }

      const existing = current.draft[kind]?.[id];
      if (!existing) return state;

      return {
        modules: {
          ...state.modules,
          [module]: {
            ...current,
            draft: {
              ...current.draft,
              [kind]: { ...current.draft[kind], [id]: { ...existing, ...patch } },
            },
          },
        },
      };
    });
  },

  create(module, kind, data) {
    const tempId = nextTempId();
    set((state) => {
      const current = state.modules[module] ?? emptyModule();
      return {
        modules: {
          ...state.modules,
          [module]: { ...current, created: { ...current.created, [tempId]: { kind, data } } },
        },
      };
    });
    return tempId;
  },

  remove(module, kind, id) {
    set((state) => {
      const current = state.modules[module] ?? emptyModule();

      // Новая запись, которую ещё не отправляли, просто исчезает —
      // серверу о ней сообщать нечего.
      if (current.created[id]) {
        const { [id]: _removed, ...rest } = current.created;
        return { modules: { ...state.modules, [module]: { ...current, created: rest } } };
      }

      return {
        modules: {
          ...state.modules,
          [module]: { ...current, deleted: { ...current.deleted, [id]: kind } },
        },
      };
    });
  },

  discard(module) {
    set((state) => {
      const current = state.modules[module];
      if (!current) return state;

      // Черновик берётся заново из нетронутого серверного снимка —
      // ровно то, что делала кнопка «Не сохранять» в демо.
      const draft: ByKind = {};
      for (const [kind, entities] of Object.entries(current.server)) {
        draft[kind] = { ...entities };
      }

      return {
        modules: {
          ...state.modules,
          [module]: { server: current.server, draft, created: {}, deleted: {} },
        },
      };
    });
  },

  rebase(module, conflicts) {
    set((state) => {
      const current = state.modules[module];
      if (!current) return state;

      const server: ByKind = {};
      for (const [kind, entities] of Object.entries(current.server)) server[kind] = { ...entities };
      const draft: ByKind = {};
      for (const [kind, entities] of Object.entries(current.draft)) draft[kind] = { ...entities };

      for (const { kind, id, actualVersion } of conflicts) {
        if (actualVersion === null) {
          // Запись удалили, пока её правили: переносить правки не на что,
          // и держать их дальше значит вечно получать тот же отказ.
          delete server[kind]?.[id];
          delete draft[kind]?.[id];
          continue;
        }
        const base = server[kind]?.[id];
        const mine = draft[kind]?.[id];
        if (!base || !mine) continue;
        server[kind] = { ...server[kind], [id]: { ...base, version: actualVersion } };
        draft[kind] = { ...draft[kind], [id]: { ...mine, version: actualVersion } };
      }

      return { modules: { ...state.modules, [module]: { ...current, server, draft } } };
    });
  },

  applyCommit(module, result) {
    set((state) => {
      const current = state.modules[module];
      if (!current) return state;

      const server: ByKind = {};
      for (const [kind, entities] of Object.entries(current.server)) {
        server[kind] = { ...entities };
      }

      // Обновлённые и созданные записи получают версии, вернувшиеся с сервера.
      const versionOf = new Map(result.entities.map((e) => [e.id, e.version]));

      for (const [kind, entities] of Object.entries(current.draft)) {
        server[kind] = server[kind] ?? {};
        for (const [id, entity] of Object.entries(entities)) {
          const version = versionOf.get(id);
          server[kind][id] = version === undefined ? entity : { ...entity, version };
        }
      }

      // Созданные записи переезжают в снимок под настоящими идентификаторами.
      for (const [tempId, { kind, data }] of Object.entries(current.created)) {
        const realId = result.tempIdMap[tempId];
        if (!realId) continue;
        server[kind] = server[kind] ?? {};
        server[kind][realId] = { ...data, id: realId, version: versionOf.get(realId) ?? 1 } as Entity;
      }

      for (const { kind, id } of result.deleted) {
        if (server[kind]) delete server[kind][id];
      }

      const draft: ByKind = {};
      for (const [kind, entities] of Object.entries(server)) draft[kind] = { ...entities };

      return { modules: { ...state.modules, [module]: { server, draft, created: {}, deleted: {} } } };
    });
  },

  isDirty(module) {
    const current = get().modules[module];
    if (!current) return false;
    if (Object.keys(current.created).length > 0) return true;
    if (Object.keys(current.deleted).length > 0) return true;

    for (const [kind, entities] of Object.entries(current.draft)) {
      for (const [id, entity] of Object.entries(entities)) {
        const original = current.server[kind]?.[id];
        if (!original) return true;
        if (Object.keys(diffEntity(original, entity)).length > 0) return true;
      }
    }
    return false;
  },

  buildCommit(module) {
    const current = get().modules[module];
    const payload: ModuleCommitPayload = { creates: [], updates: [], deletes: [] };
    if (!current) return payload;

    for (const [tempId, { kind, data }] of Object.entries(current.created)) {
      payload.creates.push({ tempId, kind, data });
    }

    for (const [kind, entities] of Object.entries(current.draft)) {
      for (const [id, entity] of Object.entries(entities)) {
        if (current.deleted[id]) continue;
        const original = current.server[kind]?.[id];
        if (!original) continue;
        const patch = diffEntity(original, entity);
        if (Object.keys(patch).length > 0) {
          payload.updates.push({ id, kind, version: original.version, patch });
        }
      }
    }

    for (const [id, kind] of Object.entries(current.deleted)) {
      payload.deletes.push({ id, kind });
    }

    return payload;
  },

  entities<T extends { id: string; version: number }>(module: ModuleName, kind: string): T[] {
    const current = get().modules[module];
    if (!current) return [];

    const existing = Object.values(current.draft[kind] ?? {}).filter((e) => !current.deleted[e.id]);

    // Новые записи получают версию 0: она никогда не уходит на сервер,
    // потому что создание отправляется отдельным списком без версии.
    const created = Object.entries(current.created)
      .filter(([, item]) => item.kind === kind)
      .map(([tempId, item]) => ({ ...item.data, id: tempId, version: 0 }) as Entity);

    return [...existing, ...created] as unknown as T[];
  },

  entity<T extends { id: string; version: number }>(
    module: ModuleName,
    kind: string,
    id: string,
  ): T | undefined {
    const current = get().modules[module];
    if (!current) return undefined;
    if (current.created[id]) {
      return { ...current.created[id]!.data, id, version: 0 } as unknown as T;
    }
    return current.draft[kind]?.[id] as unknown as T | undefined;
  },
}));

/** Есть ли несохранённые правки хотя бы в одном разделе — для `beforeunload`. */
export function anyModuleDirty(): boolean {
  const store = useDrafts.getState();
  return (Object.keys(store.modules) as ModuleName[]).some((module) => store.isDirty(module));
}
